import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  RiFileList3Line,
  RiSearchLine,
  RiRefreshLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCloseLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiMegaphoneLine,
  RiPaletteLine,
  RiCalendarLine,
  RiUserLine,
  RiTeamLine,
  RiBankLine,
  RiNewspaperLine,
  RiArrowDownSLine,
  RiShieldCheckLine,
  RiEyeLine,
  RiHashtag,
  RiFilePdf2Line,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import {
  generateQuotationOnePDF,
  QUOTATION_ONE_IMAGE_BASE64,
} from "../Util/Generatequotationonepdf";
import {
  generateQuotationTwoPDF,
  QUOTATION_TWO_IMAGE_BASE64,
} from "../Util/generateQuotationTwoPDF";
import {
  generateQuotationThreePDF,
  QUOTATION_THREE_IMAGE_BASE64,
} from "../Util/generateQuotationThreePDF";
import QuotationViewModalFour from "../QuotationViews/Quotationviewmodalfour";
import { generateQuotationBrandingPDF } from "../Util/generateQuotationBrandingPDF";
import {
  generateQuotationFourPDF,
  QUOTATION_FOUR_IMAGE_BASE64,
} from "../Util/Generatequotationfourpdf";
import QuotationViewModalOne from "../QuotationViews/QuotationViewModalOne";
import QuotationViewModalTwo from "../QuotationViews/QuotationViewModalTwo";
import QuotationViewModalThree from "../QuotationViews/Quotationviewmodalthree";

const BASE_URL = process.env.REACT_APP_API_URL;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const DeleteModal = ({ quotation, onConfirm, onClose, loading }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-sm mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-blue-100 bg-blue-50/50">
          <h2 className="text-sm font-semibold text-red-700 flex items-center gap-2">
            <RiDeleteBinLine className="w-4 h-4" /> Confirm Deletion
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
            <RiErrorWarningLine className="w-7 h-7 text-red-400" />
          </div>
          <p className="text-slate-700 font-semibold text-sm mb-1">
            Delete{" "}
            <span className="text-red-500">
              {quotation?.quotationNumber || "this quotation"}
            </span>
            ?
          </p>
          <p className="text-slate-400 text-xs mb-6">
            This action cannot be undone.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-3.5 h-3.5" /> Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-sm disabled:opacity-60"
            >
              {loading ? (
                <Spinner />
              ) : (
                <RiDeleteBinLine className="w-3.5 h-3.5" />
              )}
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );

const StatusModal = ({ quotation, onConfirm, onClose, loading }) => {
  const [selected, setSelected] = useState("");

  const options = [
    {
      value: "approved",
      label: "Approved",
      desc: "Mark this quotation as approved.",
      activeRing: "ring-emerald-400",
      activeBg: "bg-emerald-50 border-emerald-400",
      activeText: "text-emerald-700",
      dot: "bg-emerald-400",
    },
    {
      value: "rejected",
      label: "Rejected",
      desc: "Mark this quotation as rejected.",
      activeRing: "ring-red-400",
      activeBg: "bg-red-50 border-red-400",
      activeText: "text-red-600",
      dot: "bg-red-400",
    },
  ];

  return createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-sm mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-blue-100 bg-blue-50/50">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <RiShieldCheckLine className="w-4 h-4 text-indigo-500" />
            Update Status
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-blue-50/70 border border-blue-100">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
              <RiFileList3Line className="w-4 h-4 text-indigo-500" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-700 font-mono">
                {quotation?.quotationNumber || "—"}
              </p>
              <p className="text-[10px] text-slate-400">
                {quotation?.client?.bankName ||
                  quotation?.client?.name ||
                  "Unknown client"}
              </p>
            </div>
            <div className="ml-auto">
              <StatusBadge status={quotation?.status} />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
              Change status to
            </p>
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelected(opt.value)}
                disabled={quotation?.status === opt.value}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition
                  ${
                    quotation?.status === opt.value
                      ? "opacity-40 cursor-not-allowed bg-slate-50 border-slate-200"
                      : selected === opt.value
                        ? `${opt.activeBg} ring-2 ${opt.activeRing}`
                        : "bg-white border-blue-100 hover:bg-blue-50/60"
                  }`}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${opt.dot}`}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-semibold ${
                      selected === opt.value ? opt.activeText : "text-slate-700"
                    }`}
                  >
                    {opt.label}
                  </p>
                  <p className="text-[11px] text-slate-400 leading-tight">
                    {opt.desc}
                  </p>
                </div>
                {selected === opt.value && (
                  <RiCheckLine
                    className={`w-4 h-4 flex-shrink-0 ${opt.activeText}`}
                  />
                )}
                {quotation?.status === opt.value && (
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                    Current
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-blue-100 bg-blue-50/30 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
          >
            <RiCloseLine className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            onClick={() => onConfirm(selected)}
            disabled={!selected || loading || quotation?.status === selected}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition shadow-sm shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Spinner />
            ) : (
              <RiShieldCheckLine className="w-3.5 h-3.5" />
            )}
            {loading ? "Updating…" : "Update Status"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Badges ───────────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-600 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
        map[status] || map.pending
      }`}
    >
      {status || "pending"}
    </span>
  );
};

const TypeBadge = ({ type }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest border
    ${
      type === "branding"
        ? "bg-purple-50 text-purple-700 border-purple-200"
        : "bg-blue-50 text-blue-700 border-blue-200"
    }`}
  >
    {type === "branding" ? (
      <RiPaletteLine className="w-3 h-3" />
    ) : (
      <RiMegaphoneLine className="w-3 h-3" />
    )}
    {type || "advertising"}
  </span>
);

const selectFilterCls =
  "bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-blue-500 transition appearance-none cursor-pointer pr-7";

const SelectFilterWrapper = ({ children }) => (
  <div className="relative">
    {children}
    <RiArrowDownSLine className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AllQuotation() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pdfPreviewTarget, setPdfPreviewTarget] = useState(null);
  const [previewModalType, setPreviewModalType] = useState(null); // 'one' or 'two'
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const location = useLocation();

  const notify = (msg, type = "success") => setToast({ message: msg, type });

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/quotation/get-all`);
      setQuotations(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch quotations", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
    window.addEventListener("focus", fetchQuotations);
    return () => window.removeEventListener("focus", fetchQuotations);
  }, []);

  const getCategoryName = (q) => {
    if (q.quotationCategory?.name) return q.quotationCategory.name;
    if (typeof q.quotationCategory === "string") return q.quotationCategory;
    if (q.category?.name) return q.category.name;
    if (typeof q.category === "string") return q.category;
    if (q.advertisingType?.name) return q.advertisingType.name;
    if (typeof q.advertisingType === "string") return q.advertisingType;
    return "";
  };

  const isCategoryOne = (q) => getCategoryName(q).includes("1");
  const isCategoryTwo = (q) => getCategoryName(q).includes("2");
  const isCategoryThree = (q) => getCategoryName(q).includes("3");
  const isCategoryFour = (q) => getCategoryName(q).includes("4");

  // ── Build payload for QuotationViewModalOne ──────────────────────────────
  const buildQuotationOnePayload = (q) => ({
    quotationNumber: q.quotationNumber || "",
    date: q.date || "",
    client: q.client || {},
    personName: q.personName || "",
    mediaRateCardItems: q.mediaRateCardItems || [],
    totalAmount: q.totalAmount || 0,
    amountInWords: q.amountInWords || "",
    selectedQuery: q.selectedQuery || null,
    company: q.company || {},
    advertisingType: q?.advertisingType?.name || "",
  });

  // ── Build payload for QuotationViewModalTwo ──────────────────────────────
  const buildQuotationTwoPayload = (q) => ({
    quotationNumber: q.quotationNumber || "",
    date: q.date || "",
    client: q.client || {},
    personName: q.personName || "",
    mediaRateCardItems: q.mediaRateCardItems || [],
    totalAmount: q.totalAmount || 0,
    amountInWords: q.amountInWords || "",
    selectedQuery: q.selectedQuery || null,
    company: q.company || {},
    advertisingType: q?.advertisingType?.name || "",
  });

  const handleOpenPdfPreview = (q) => {
    if (q.quotationType === "advertising") {
      if (isCategoryOne(q)) {
        setPreviewModalType("one");
        setPdfPreviewTarget(q);
      } else if (isCategoryTwo(q)) {
        setPreviewModalType("two");
        setPdfPreviewTarget(q);
      } else if (isCategoryThree(q)) {
        setPreviewModalType("three");
        setPdfPreviewTarget(q);
      } else if (isCategoryFour(q)) {
        setPreviewModalType("four");
        setPdfPreviewTarget(q);
      } else {
        handleDownloadPDF(q);
      }
    } else if (q.quotationType === "branding") {
      handleDownloadPDF(q);
    }
  };

  const handleDownloadPDF = (q) => {
    const payload = {
      quotationNumber: q.quotationNumber || "",
      date: q.date || "",
      client: q.client || {},
      personName: q.personName || "",
      mediaRateCardItems: q.mediaRateCardItems || [],
      brandingPricing: q.brandingPricing || {},
      totalAmount: q.totalAmount || 0,
      amountInWords: q.amountInWords || "",
      selectedQuery: q.selectedQuery || null,
      company: q.company || {},
      advertisingType: q?.advertisingType?.name || {},
    };

    if (q.quotationType === "branding") {
      generateQuotationBrandingPDF(payload);
    } else if (q.quotationType === "advertising") {
      if (isCategoryOne(q)) {
        generateQuotationOnePDF(payload);
      } else if (isCategoryTwo(q)) {
        generateQuotationTwoPDF(payload);
      } else if (isCategoryThree(q)) {
        generateQuotationThreePDF(payload);
      } else if (isCategoryFour(q)) {
        generateQuotationFourPDF(payload);
      } else {
        notify("PDF template not available for this category", "error");
      }
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${BASE_URL}/quotation/delete/${deleteTarget._id}`);
      notify("Quotation deleted successfully");
      setDeleteTarget(null);
      fetchQuotations();
    } catch {
      notify("Failed to delete quotation", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!newStatus || !statusTarget) return;
    setStatusLoading(true);
    try {
      await axios.patch(`${BASE_URL}/quotation/${statusTarget._id}/status`, {
        status: newStatus,
      });
      notify(`Quotation marked as ${newStatus}`);
      setQuotations((prev) =>
        prev.map((q) =>
          q._id === statusTarget._id ? { ...q, status: newStatus } : q,
        ),
      );
      setStatusTarget(null);
    } catch (err) {
      notify(err.response?.data?.message || "Failed to update status", "error");
    } finally {
      setStatusLoading(false);
    }
  };

  const filtered = quotations.filter((q) => {
    const clientName = q.client?.bankName || q.client?.name || "";
    const qNum = q.quotationNumber || "";
    const matchSearch =
      clientName.toLowerCase().includes(search.toLowerCase()) ||
      qNum.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? q.status === filterStatus : true;
    const matchType = filterType ? q.quotationType === filterType : true;
    return matchSearch && matchStatus && matchType;
  });

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const getItemsCount = (quotation) => {
    if (quotation.quotationType === "advertising") {
      return quotation.mediaRateCardItems?.length || 0;
    } else if (
      quotation.quotationType === "branding" &&
      quotation.brandingPricing
    ) {
      return quotation.brandingPricing.numberOfPersons || 0;
    }
    return 0;
  };

  const getItemsDisplay = (quotation) => {
    if (quotation.quotationType === "advertising") {
      const count = quotation.mediaRateCardItems?.length || 0;
      return `${count} Media Rate Card${count !== 1 ? "s" : ""}`;
    } else if (
      quotation.quotationType === "branding" &&
      quotation.brandingPricing
    ) {
      return `${quotation.brandingPricing.numberOfPersons || 0} ${
        quotation.brandingPricing.numberOfPersons === 1 ? "Person" : "Persons"
      }`;
    }
    return "—";
  };

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-md shadow-indigo-200">
              <RiFileList3Line className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-800 tracking-tight">
                All Quotations
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Quotation Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by client or number…"
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-56 transition"
              />
            </div>

            <SelectFilterWrapper>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className={selectFilterCls}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </SelectFilterWrapper>

            <SelectFilterWrapper>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className={selectFilterCls}
              >
                <option value="">All Types</option>
                <option value="advertising">Advertising</option>
                <option value="branding">Branding</option>
              </select>
            </SelectFilterWrapper>

            <button
              onClick={fetchQuotations}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <div className="px-6 pt-6 pb-10">
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiFileList3Line className="w-4 h-4 text-blue-500" />
              Quotations
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {[
                    { label: "Quot. No." },
                    { label: "Type" },
                    { label: "Client", Icon: RiBankLine },
                    { label: "Category", Icon: RiUserLine },
                    { label: "Referral", Icon: RiTeamLine },
                    { label: "Items/Persons", Icon: RiNewspaperLine },
                    { label: "Date", Icon: RiCalendarLine },
                    { label: "Status" },
                    { label: "Actions" },
                  ].map(({ label, Icon }) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {Icon && <Icon className="w-3 h-3" />}
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <Spinner className="w-7 h-7 text-indigo-500" />
                        <span className="text-sm">Loading quotations…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiFileList3Line className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search || filterStatus || filterType
                            ? "No quotations match your filters."
                            : "No quotations found."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((q, i) => (
                    <tr
                      key={q._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/40 transition group"
                    >
                      <td className="px-4 py-3 font-mono font-semibold text-indigo-700 whitespace-nowrap">
                        {q.quotationNumber || "—"}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <TypeBadge type={q.quotationType} />
                      </td>

                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {q.client?.bankName || q.client?.name || "—"}
                      </td>

                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {q?.quotationCategory?.name || "—"}
                      </td>

                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {q.referral?.name || "—"}
                      </td>

                      <td className="px-4 py-3">
                        <div className="group/item relative">
                          <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2 rounded-full bg-blue-50 border border-blue-200 text-xs font-bold text-blue-600">
                            {getItemsCount(q)}
                          </span>
                          {getItemsCount(q) > 0 && (
                            <div className="absolute left-0 bottom-full mb-2 hidden group-hover/item:block z-10">
                              <div className="bg-slate-800 text-white text-[10px] rounded-lg px-3 py-2 whitespace-nowrap">
                                {getItemsDisplay(q)}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                        {formatDate(q.date)}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => setStatusTarget(q)}
                          title="Click to change status"
                          className="group/status flex items-center gap-1.5 transition"
                        >
                          <StatusBadge status={q.status} />
                          <RiShieldCheckLine className="w-3 h-3 text-slate-300 group-hover/status:text-indigo-400 transition" />
                        </button>
                      </td>

                      {/* ── Actions ── */}
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {q.quotationType === "advertising" &&
                            (isCategoryOne(q) ||
                              isCategoryTwo(q) ||
                              isCategoryThree(q) ||
                              isCategoryFour(q)) && (
                              <button
                                onClick={() => handleOpenPdfPreview(q)}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-sky-50 border border-sky-200 text-sky-600 hover:bg-sky-100 font-semibold transition"
                                title={`Preview ${isCategoryOne(q) ? "Category 1" : "Category 2"} Quotation`}
                              >
                                <RiEyeLine className="w-3.5 h-3.5" /> View
                              </button>
                            )}

                          {/* Edit */}
                          <button
                            onClick={() =>
                              navigate(`/admin/edit-quotation/${q._id}`)
                            }
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-600 hover:bg-indigo-100 font-semibold transition"
                          >
                            <RiEditLine className="w-3.5 h-3.5" /> Edit
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => setDeleteTarget(q)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
                          </button>

                          {/* PDF/Preview Button */}
                          <button
                            onClick={() => handleDownloadPDF(q)}
                            disabled={
                              q.quotationType === "advertising" &&
                              !isCategoryOne(q) &&
                              !isCategoryTwo(q) &&
                              !isCategoryThree(q) &&
                              !isCategoryFour(q)
                            }
                            title={`Download PDF — ${q.quotationNumber}`}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg
    bg-rose-50 border border-rose-200 text-rose-600
    hover:bg-rose-100 font-semibold transition
    disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <RiFilePdf2Line className="w-3.5 h-3.5" />
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}

      {deleteTarget && (
        <DeleteModal
          quotation={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          loading={deleteLoading}
        />
      )}

      {statusTarget && (
        <StatusModal
          quotation={statusTarget}
          onConfirm={handleStatusUpdate}
          onClose={() => setStatusTarget(null)}
          loading={statusLoading}
        />
      )}

      {/* ── QuotationViewModalOne — PDF preview for Category 1 ── */}
      {pdfPreviewTarget &&
        previewModalType === "one" &&
        isCategoryOne(pdfPreviewTarget) && (
          <QuotationViewModalOne
            quotationData={buildQuotationOnePayload(pdfPreviewTarget)}
            onClose={() => {
              setPdfPreviewTarget(null);
              setPreviewModalType(null);
            }}
            bgBase64={QUOTATION_ONE_IMAGE_BASE64}
          />
        )}

      {pdfPreviewTarget &&
        previewModalType === "two" &&
        isCategoryTwo(pdfPreviewTarget) && (
          <QuotationViewModalTwo
            open={true}
            quotationData={buildQuotationTwoPayload(pdfPreviewTarget)}
            bgBase64={QUOTATION_TWO_IMAGE_BASE64}
            onClose={() => {
              setPdfPreviewTarget(null);
              setPreviewModalType(null);
            }}
          />
        )}

      {pdfPreviewTarget &&
        previewModalType === "three" &&
        isCategoryThree(pdfPreviewTarget) && (
          <QuotationViewModalThree
            open={true}
            quotationData={buildQuotationOnePayload(pdfPreviewTarget)}
            bgBase64={QUOTATION_THREE_IMAGE_BASE64}
            onClose={() => {
              setPdfPreviewTarget(null);
              setPreviewModalType(null);
            }}
          />
        )}

      {pdfPreviewTarget &&
        previewModalType === "four" &&
        isCategoryFour(pdfPreviewTarget) && (
          <QuotationViewModalFour
            open={true}
            quotationData={buildQuotationOnePayload(pdfPreviewTarget)}
            bgBase64={QUOTATION_FOUR_IMAGE_BASE64}
            onClose={() => {
              setPdfPreviewTarget(null);
              setPreviewModalType(null);
            }}
          />
        )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
